/**
 * ----------------------------------------------------------------------------
 * ByProcessChart Component
 * ----------------------------------------------------------------------------
 * Summary:
 * Component for rendering nonconformity counts grouped by process as a
 * responsive bar chart. Fetches the process list and chart data based on
 * the selected date range and checked process filters, then displays the
 * results with gradient bar styling, value labels, and tooltips.
 *
 * @author Chandan Singh
 * @created September-2026
 * ----------------------------------------------------------------------------
 */
import { observer } from "mobx-react"
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts"
import { useStore } from "../../../contexts/store-provider"
import "./nc-bar-chart.css"
import { useEffect, useState } from "react"
import noMessageIcon from '../../../assets/Images/no-noconfirmitry.png'
import { FormattedMessage } from "react-intl"
import { formatMessage } from "../../../translations/format-message"
import { CustomXAxisTick } from "./nc-chart-label"
interface ChartProps {
  fromDate: string
  toDate: string
}

function ByProcessChart(props: ChartProps) {
  const { nonconformityStore } = useStore()
  const {
    NCReportChartState,
    GetChartDataByProcess,
    GetAllProcessService,
    getByProcessCheckedValues,
    byProcessChartData,
    allProcessOptions,
    UpdateColumnSelection,
    getNonconformitySummaryDetails
  } = nonconformityStore

  const { fromDate, toDate } = props
  const [isListLoaded, setIsListLoaded] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false)
  useEffect(() => {
    const fetchList = async () => {
      await GetAllProcessService()
      setIsListLoaded(true)
    }
    fetchList()
  }, [fromDate, toDate])

  useEffect(() => {
    if (!isListLoaded) return;
    if (!getByProcessCheckedValues || !getByProcessCheckedValues.length) return;

    GetChartDataByProcess(fromDate, toDate, getByProcessCheckedValues)
  }, [isListLoaded, getByProcessCheckedValues])

  const allChecked =
    allProcessOptions.length > 0 &&
    getByProcessCheckedValues.length === allProcessOptions.length

  const filteredProcessOptions = allProcessOptions.filter((o) =>
    formatMessage(o.value as string)
      .toLowerCase()
      .includes(searchText.trim().toLowerCase())
  )

  const toggleProcess =(id: number) => {
    const next = getByProcessCheckedValues.includes(id)
      ? getByProcessCheckedValues.filter((x) => x !== id)
      : // keep the original process order
        allProcessOptions
          .map((o) => Number(o.id))
          .filter((x) => x === id || getByProcessCheckedValues.includes(x))
    UpdateColumnSelection(next)
  }

  const toggleAll = () => {
    UpdateColumnSelection(allChecked ? [] : allProcessOptions.map((o) => Number(o.id)))
  }

  // Totals come from the chart data, so only checked processes have a count.
  const getProcessTotal = (processName: string): number | undefined => {
    const label = formatMessage(processName)
    const row = byProcessChartData?.ChartReportList?.find(
      (r: any) => r.DisplayName === label || r.DisplayName === processName
    )
    return row?.TotalCount
  }

  return (
    <div className="nc-process-view">
      <div className="nc-stat-row">
        {getNonconformitySummaryDetails.map((card) => (
          <div key={card.labelId} className={`nc-stat-card ${card.variant}`}>
            <div className="nc-stat-label">
              <FormattedMessage id={card.labelId} />
            </div>
            <div className="nc-stat-value">
              {card.value}
              <span className="nc-stat-unit">
                <FormattedMessage id="nc_requests" />
              </span>
            </div>
            <div className="nc-stat-progress">
              {/* Total card shows a plain line; the hidden percent keeps its height equal to the other cards. */}
              <div className="nc-stat-track" style={card.labelId === "nc_total_trays" ? { height: 4, background: "#fff", marginTop: "7px" } : undefined}>
                {card.labelId !== "nc_total_trays" && (
                  <div className="nc-stat-fill" style={{ width: `${card.percent}%` }} />
                )}
              </div>
              {card.labelId !== "nc_total_trays" &&
                <span className="nc-stat-percent">{card.percent}%</span>
              }
            </div>
          </div>
        ))}
      </div>

      <div className={`nc-chart-row${isPanelCollapsed ? " nc-panel-collapsed" : ""}`}>
        <div className="nc-chart-container nc-chart-process nc-chart-main">
      {!isListLoaded ? null : !getByProcessCheckedValues.length ? (
        <div className="message">
          <div className="d-flex message-area">
            <div className="no-data-img">
              <img
                src={noMessageIcon}
                alt=""
                className="no_message"
              />
            </div>
            <div>
              <h3 className="fw-bold display-6">
                {' '}
                <FormattedMessage id="no_column_selected" /><br />
                <FormattedMessage id="please_select_a_column" />
              </h3>
            </div>
          </div>
        </div>
      ) : !byProcessChartData || !byProcessChartData?.ChartReportList?.length ? (
        <div className="message">
          <div className="d-flex message-area">
            <div className="no-data-img">
              <img
                src={noMessageIcon}
                alt=""
                className="no_message"
              />
            </div>
            <div>
              <h3 className="fw-bold display-6">
                {' '}
                <FormattedMessage id="no_nonconformity" />
              </h3>
            </div>
          </div>
        </div>
      ) : (

        <ResponsiveContainer width="100%" height="100%" className="nc-chart-canvas">
          <BarChart data={byProcessChartData.ChartReportList} margin={{ top: 24, right: 24, left: 0, bottom: 8 }} barCategoryGap="30%">
            <defs>
              <linearGradient id="ncBarProcessGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" className="nc-bar-gradient-start" />
                <stop offset="100%" className="nc-bar-gradient-end" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="DisplayName" className="nc-chart-axis-x" axisLine={false} tickLine={false}  angle={45} textAnchor="start" tickMargin={3} height={100} tick={<CustomXAxisTick />}/>
            <YAxis allowDecimals={false} className="nc-chart-axis-y" axisLine={false} tickLine={false} />
            <Tooltip cursor={{ className: "nc-chart-cursor" }} formatter={(value) => [`${value}`,formatMessage("total_count")]}/>
            <Bar
              dataKey="TotalCount"
              fill="var(--nc-bar-tint)"
              radius={[8, 8, 0, 0]}
              maxBarSize={64}
              className="nc-bar-rect"
              activeBar={{ fill: "url(#ncBarProcessGradient)", className: "nc-bar-rect nc-bar-active" }}
            >
              <LabelList dataKey="TotalCount" position="top" className="nc-chart-label" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
        </div>

        {isPanelCollapsed && (
          <button
            type="button"
            className="nc-legend-strip"
            onClick={() => setIsPanelCollapsed(false)}
            aria-label="Expand process panel"
            title={formatMessage("expand")}
          >
            <span className="nc-legend-strip-icon">&laquo;</span>
            <span className="nc-legend-strip-text">
              <FormattedMessage id="nc_processes" />
            </span>
          </button>
        )}

        <aside className="nc-legend-panel">
          <div className="nc-legend-title">
            <FormattedMessage id="nc_processes" />
            <button
              type="button"
              className="nc-legend-toggle"
              onClick={() => setIsPanelCollapsed(true)}
              aria-label="Collapse process panel"
              title={formatMessage("collapse")}
            >
              &raquo;
            </button>
          </div>
          <div className="nc-legend-body">
          <div className="nc-legend-all">
            <label className="nc-legend-item">
              <input type="checkbox" checked={allChecked} onChange={toggleAll} />
              <span><FormattedMessage id="select_all_options" /></span>
            </label>
            <div className="nc-legend-search-wrap">
              <input
                type="text"
                className="nc-legend-search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={formatMessage("search")}
              />
              {searchText && (
                <button
                  type="button"
                  className="nc-legend-search-clear"
                  onClick={() => setSearchText("")}
                  aria-label="Clear search"
                >
                  &times;
                </button>
              )}
            </div>
          </div>
          <div className="nc-legend-list">
            {filteredProcessOptions.map((opt) => (
              <label key={opt.id} className="nc-legend-item">
                <input
                  type="checkbox"
                  checked={getByProcessCheckedValues.includes(Number(opt.id))}
                  onChange={() => toggleProcess(Number(opt.id))}
                />
                <span className="nc-legend-name">{formatMessage(opt.value as string)}</span>
                <span className="nc-legend-count">
                  {getByProcessCheckedValues.includes(Number(opt.id))
                    ? getProcessTotal(opt.value as string) ?? 0
                    : null}
                </span>
              </label>
            ))}
          </div>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default observer(ByProcessChart)
